/**
 * AI marketing campaigns — thunks wrapping campaignsApi.js.
 * Tracks list, current campaign, brand kit, defaults, analytics, and action loading.
 */
import {createSlice} from "@reduxjs/toolkit";
import {formatAxiosErrorMessage} from "../../utils/common";
import {
    cancelCampaign,
    createCampaign,
    deleteCampaign,
    downloadCreative,
    editCreative,
    enhancePrompt,
    exportCampaignButtonReplies,
    generateCampaign,
    getBrandKit,
    getCampaign,
    getCampaignAnalytics,
    getCampaignButtonReplies,
    getCreativeDefaults,
    getGenerationJob,
    listCampaigns,
    listCreativeVersions,
    patchCampaign,
    regenerateCreative,
    restoreCreativeVersion,
    runCampaign,
    setAudience,
    setRunMode,
    submitTemplate,
    testSend,
    updateBrandKit,
} from "../../api/campaignsApi";
import {waitForCampaignJob} from "../../api/realtimeSocket";

const unwrap = (payload) => payload?.data ?? payload ?? null;

const slot = () => ({isLoading: false, data: null, error: null});

/** Merge campaign fields into list.data when that id is already in the store. */
const mergeCampaignIntoList = (state, campaign) => {
    if (!campaign?.id || !Array.isArray(state.list.data)) return;
    const idx = state.list.data.findIndex((row) => row.id === campaign.id);
    if (idx === -1) return;
    state.list.data[idx] = {...state.list.data[idx], ...campaign};
};

const initialState = {
    list: {isLoading: false, data: [], count: 0, error: null},
    campaign: slot(),
    brandKit: slot(),
    defaults: slot(),
    analytics: slot(),
    // Paginated clients who replied to a selected button option
    buttonReplies: {
        isLoading: false,
        isLoadingMore: false,
        isExporting: false,
        data: [],
        count: 0,
        page: 1,
        buttonIndex: null,
        buttonText: "",
        error: null,
    },
    action: slot(),
    // Creative generation history (newest first); selectedVersionId tracks UI dropdown
    creativeVersions: {
        isLoading: false,
        data: [],
        selectedVersionId: null,
        error: null,
    },
    // True from generate POST until job finalize — independent of persist/enhance action slot
    generationPending: false,
};

/** 402 / insufficient credits so the UI can send the user to /credits. */
const creditOrMessage = (err) => {
    const body = err?.response?.data;
    const code = Array.isArray(body?.code) ? body.code[0] : body?.code;
    if (code === "insufficient_credits" || err?.response?.status === 402) {
        return {
            code: "insufficient_credits",
            detail: body?.detail || formatAxiosErrorMessage(err),
        };
    }
    return formatAxiosErrorMessage(err);
};

export const aiCampaignSlice = createSlice({
    name: "aiCampaigns",
    initialState,
    reducers: {
        listRequesting: (state) => {
            state.list.isLoading = true;
            state.list.error = null;
        },
        listSuccess: (state, action) => {
            state.list.isLoading = false;
            const body = unwrap(action.payload);
            if (Array.isArray(body)) {
                state.list.data = body;
                state.list.count = body.length;
            } else {
                state.list.data = body?.results || [];
                state.list.count = body?.count ?? state.list.data.length;
            }
        },
        listError: (state, action) => {
            state.list.isLoading = false;
            state.list.error = action.payload;
        },
        campaignRequesting: (state) => {
            state.campaign.isLoading = true;
            state.campaign.error = null;
        },
        campaignSuccess: (state, action) => {
            state.campaign.isLoading = false;
            const data = unwrap(action.payload);
            state.campaign.data = data;
            mergeCampaignIntoList(state, data);
        },
        campaignError: (state, action) => {
            state.campaign.isLoading = false;
            state.campaign.error = action.payload;
        },
        campaignReset: (state) => {
            state.campaign = slot();
            state.analytics = slot();
            state.buttonReplies = {
                isLoading: false,
                isLoadingMore: false,
                isExporting: false,
                data: [],
                count: 0,
                page: 1,
                buttonIndex: null,
                buttonText: "",
                error: null,
            };
            state.creativeVersions = {
                isLoading: false,
                data: [],
                selectedVersionId: null,
                error: null,
            };
            state.action = slot();
            state.generationPending = false;
        },
        creativeVersionsRequesting: (state) => {
            state.creativeVersions.isLoading = true;
            state.creativeVersions.error = null;
        },
        creativeVersionsSuccess: (state, action) => {
            state.creativeVersions.isLoading = false;
            const rows = unwrap(action.payload);
            const list = Array.isArray(rows) ? rows : [];
            state.creativeVersions.data = list;
            const current =
                list.find((row) => row.is_current) ||
                list[0] ||
                null;
            state.creativeVersions.selectedVersionId = current?.id || null;
        },
        creativeVersionsError: (state, action) => {
            state.creativeVersions.isLoading = false;
            state.creativeVersions.error = action.payload;
        },
        creativeVersionSelect: (state, action) => {
            state.creativeVersions.selectedVersionId = action.payload || null;
        },
        brandKitRequesting: (state) => {
            state.brandKit.isLoading = true;
            state.brandKit.error = null;
        },
        brandKitSuccess: (state, action) => {
            state.brandKit.isLoading = false;
            state.brandKit.data = unwrap(action.payload);
        },
        brandKitError: (state, action) => {
            state.brandKit.isLoading = false;
            state.brandKit.error = action.payload;
        },
        defaultsRequesting: (state) => {
            state.defaults.isLoading = true;
            state.defaults.error = null;
        },
        defaultsSuccess: (state, action) => {
            state.defaults.isLoading = false;
            state.defaults.data = unwrap(action.payload);
        },
        defaultsError: (state, action) => {
            state.defaults.isLoading = false;
            state.defaults.error = action.payload;
        },
        analyticsRequesting: (state) => {
            state.analytics.isLoading = true;
            state.analytics.error = null;
        },
        analyticsSuccess: (state, action) => {
            state.analytics.isLoading = false;
            state.analytics.data = unwrap(action.payload);
        },
        analyticsError: (state, action) => {
            state.analytics.isLoading = false;
            state.analytics.error = action.payload;
        },
        buttonRepliesRequesting: (state, action) => {
            const append = Boolean(action.payload?.append);
            state.buttonReplies.error = null;
            if (append) {
                state.buttonReplies.isLoadingMore = true;
            } else {
                state.buttonReplies.isLoading = true;
                state.buttonReplies.data = [];
                state.buttonReplies.count = 0;
                state.buttonReplies.page = 1;
            }
        },
        buttonRepliesSuccess: (state, action) => {
            const {
                results = [],
                count = 0,
                page = 1,
                append = false,
                button_index = null,
                button_text = "",
            } = action.payload || {};
            state.buttonReplies.isLoading = false;
            state.buttonReplies.isLoadingMore = false;
            state.buttonReplies.count = count;
            state.buttonReplies.page = page;
            state.buttonReplies.buttonIndex = button_index;
            state.buttonReplies.buttonText = button_text;
            state.buttonReplies.data = append
                ? [...(state.buttonReplies.data || []), ...results]
                : results;
        },
        buttonRepliesError: (state, action) => {
            state.buttonReplies.isLoading = false;
            state.buttonReplies.isLoadingMore = false;
            state.buttonReplies.error = action.payload;
        },
        buttonRepliesReset: (state) => {
            state.buttonReplies = {
                isLoading: false,
                isLoadingMore: false,
                isExporting: false,
                data: [],
                count: 0,
                page: 1,
                buttonIndex: null,
                buttonText: "",
                error: null,
            };
        },
        buttonRepliesExportRequesting: (state) => {
            state.buttonReplies.isExporting = true;
            state.buttonReplies.error = null;
        },
        buttonRepliesExportSuccess: (state) => {
            state.buttonReplies.isExporting = false;
        },
        buttonRepliesExportError: (state, action) => {
            state.buttonReplies.isExporting = false;
            state.buttonReplies.error = action.payload;
        },
        actionRequesting: (state) => {
            state.action.isLoading = true;
            state.action.error = null;
            state.action.data = null;
        },
        actionSuccess: (state, action) => {
            state.action.isLoading = false;
            state.action.data = unwrap(action.payload);
            // Mirror campaign-shaped payloads only — not CampaignGenerationJob rows
            const data = state.action.data;
            if (data?.id && !data?.job_type) {
                state.campaign.data = data;
                mergeCampaignIntoList(state, data);
            }
        },
        actionError: (state, action) => {
            state.action.isLoading = false;
            state.action.error = action.payload;
        },
        actionReset: (state) => {
            state.action = slot();
        },
        generationPendingSet: (state, action) => {
            state.generationPending = Boolean(action.payload);
        },
        // Drop a campaign from the list after a successful delete
        removeCampaignFromList: (state, action) => {
            const id = action.payload;
            if (!id || !Array.isArray(state.list.data)) return;
            const prevLen = state.list.data.length;
            state.list.data = state.list.data.filter((row) => row.id !== id);
            if (state.list.data.length < prevLen) {
                state.list.count = Math.max(0, (state.list.count || 0) - 1);
            }
            if (state.campaign.data?.id === id) {
                state.campaign = slot();
            }
        },
        // Merge updated campaign fields into the list row (e.g. after cancel)
        updateCampaignInList: (state, action) => {
            const row = action.payload;
            if (!row?.id || !Array.isArray(state.list.data)) return;
            state.list.data = state.list.data.map((item) =>
                item.id === row.id ? {...item, ...row} : item
            );
            if (state.campaign.data?.id === row.id) {
                state.campaign.data = {...state.campaign.data, ...row};
            }
        },
    },
});

export const {
    listRequesting,
    listSuccess,
    listError,
    campaignRequesting,
    campaignSuccess,
    campaignError,
    campaignReset,
    brandKitRequesting,
    brandKitSuccess,
    brandKitError,
    defaultsRequesting,
    defaultsSuccess,
    defaultsError,
    analyticsRequesting,
    analyticsSuccess,
    analyticsError,
    buttonRepliesRequesting,
    buttonRepliesSuccess,
    buttonRepliesError,
    buttonRepliesReset,
    buttonRepliesExportRequesting,
    buttonRepliesExportSuccess,
    buttonRepliesExportError,
    actionRequesting,
    actionSuccess,
    actionError,
    actionReset,
    generationPendingSet,
    removeCampaignFromList,
    updateCampaignInList,
    creativeVersionsRequesting,
    creativeVersionsSuccess,
    creativeVersionsError,
    creativeVersionSelect,
} = aiCampaignSlice.actions;

export const AiCampaignState = (state) => state.aiCampaigns;

/** Poll job status (HTTP fallback when realtime socket is down). */
const pollGenerationJob = (jobId) =>
    getGenerationJob(jobId).then((res) => unwrap(res.data));

/**
 * After socket/poll terminal event, fetch full job row (+ campaign) and update store.
 * If the job is still non-terminal (e.g. overall wait timed out), surface an error.
 */
const finalizeGenerationJob = (dispatch, jobId) =>
    pollGenerationJob(jobId).then((job) => {
        if (job?.status === "failed") {
            if (job?.campaign) {
                dispatch(campaignSuccess(job.campaign));
            }
            const msg = job.error || "Generation failed";
            dispatch(actionError(msg));
            throw new Error(msg);
        }
        if (job?.status !== "succeeded") {
            const msg = "Generation timed out";
            dispatch(actionError(msg));
            throw new Error(msg);
        }
        const done = (campaignData) => {
            if (campaignData) {
                dispatch(campaignSuccess(campaignData));
                // Refresh history tiles and auto-select newest / current version
                if (campaignData.id) {
                    dispatch(fetchCreativeVersions(campaignData.id));
                }
            }
            dispatch(actionSuccess(job));
            return job;
        };
        if (job?.campaign) {
            return done(job.campaign);
        }
        if (job?.campaign_id) {
            return getCampaign(job.campaign_id).then((res) => done(unwrap(res.data)));
        }
        dispatch(actionSuccess(job));
        return job;
    });

/**
 * POST returns 202 { job_id }; wait for campaign.job socket (poll only if socket down).
 */
const runAsyncGeneration = (dispatch, getState, startRequest) => {
    dispatch(actionRequesting());
    dispatch(generationPendingSet(true));
    const current = getState().aiCampaigns?.campaign?.data;
    if (current) {
        dispatch(campaignSuccess({...current, status: "generating"}));
    }
    return startRequest()
        .then((res) => {
            const body = unwrap(res.data);
            const jobId = body?.job_id;
            // Keep local generating status while waiting (create/patch may have written draft)
            const afterStart = getState().aiCampaigns?.campaign?.data;
            if (afterStart) {
                dispatch(campaignSuccess({...afterStart, status: "generating"}));
            }
            // Legacy sync path if backend ever returns campaign directly
            if (!jobId) {
                dispatch(generationPendingSet(false));
                dispatch(actionSuccess(res.data));
                if (body?.id) {
                    dispatch(campaignSuccess(body));
                }
                return body;
            }
            return waitForCampaignJob(jobId, pollGenerationJob)
                .then(() => finalizeGenerationJob(dispatch, jobId))
                .catch(() => finalizeGenerationJob(dispatch, jobId))
                .finally(() => dispatch(generationPendingSet(false)));
        })
        .catch((err) => {
            dispatch(generationPendingSet(false));
            if (err instanceof Error && err.message && !err.response) {
                throw err;
            }
            dispatch(actionError(creditOrMessage(err)));
            throw err;
        });
};

export const fetchAiCampaigns = (query = {}) => (dispatch) => {
    dispatch(listRequesting());
    return listCampaigns(query)
        .then((res) => {
            dispatch(listSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(listError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const fetchAiCampaign = (id) => (dispatch) => {
    dispatch(campaignRequesting());
    return getCampaign(id)
        .then((res) => {
            dispatch(campaignSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(campaignError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

/** Load org brand kit once; pass force=true to refresh (e.g. after save). */
export const fetchBrandKit =
    ({force = false} = {}) =>
    (dispatch, getState) => {
        const {brandKit} = getState().aiCampaigns || {};
        // Skip repeat GETs while opening the popover again
        if (!force && brandKit?.data != null) {
            return Promise.resolve(brandKit.data);
        }
        if (!force && brandKit?.isLoading) {
            return Promise.resolve(brandKit?.data ?? null);
        }
        dispatch(brandKitRequesting());
        return getBrandKit()
            .then((res) => {
                dispatch(brandKitSuccess(res.data));
                return unwrap(res.data);
            })
            .catch((err) => {
                dispatch(brandKitError(formatAxiosErrorMessage(err)));
                throw err;
            });
    };

export const saveBrandKit = (formData) => (dispatch) => {
    dispatch(brandKitRequesting());
    return updateBrandKit(formData)
        .then((res) => {
            // Save response is the refreshed kit (no extra GET)
            dispatch(brandKitSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(brandKitError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const fetchCreativeDefaults = () => (dispatch) => {
    dispatch(defaultsRequesting());
    return getCreativeDefaults()
        .then((res) => {
            dispatch(defaultsSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(defaultsError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const fetchCampaignAnalytics = (id) => (dispatch) => {
    dispatch(analyticsRequesting());
    return getCampaignAnalytics(id)
        .then((res) => {
            dispatch(analyticsSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(analyticsError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

const PAGE_SIZE = 20;

/** Paginated clients for one button option; append=true loads next page. */
export const fetchCampaignButtonReplies =
    (id, {button_index, page = 1, append = false, limit = PAGE_SIZE} = {}) =>
    (dispatch) => {
        if (button_index === null || button_index === undefined || button_index === "") {
            return Promise.reject(new Error("button_index is required"));
        }
        dispatch(buttonRepliesRequesting({append}));
        return getCampaignButtonReplies(id, {
            button_index,
            page,
            limit,
        })
            .then((res) => {
                const body = unwrap(res.data) || {};
                const results = Array.isArray(body.results)
                    ? body.results
                    : Array.isArray(body)
                      ? body
                      : [];
                dispatch(
                    buttonRepliesSuccess({
                        results,
                        count: body.count ?? results.length,
                        page,
                        append,
                        button_index: body.button_index ?? button_index,
                        button_text: body.button_text || "",
                    })
                );
                return body;
            })
            .catch((err) => {
                dispatch(buttonRepliesError(formatAxiosErrorMessage(err)));
                throw err;
            });
    };

/** Download CSV of button replies (selected option or all when button_index omitted). */
export const exportCampaignButtonRepliesAPI =
    (id, {button_index} = {}) =>
    (dispatch) => {
        dispatch(buttonRepliesExportRequesting());
        const params = {};
        if (button_index !== null && button_index !== undefined && button_index !== "") {
            params.button_index = button_index;
        }
        return exportCampaignButtonReplies(id, params)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    `campaign-${id}-button-replies.csv`
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                dispatch(buttonRepliesExportSuccess());
                return true;
            })
            .catch((err) => {
                dispatch(buttonRepliesExportError(formatAxiosErrorMessage(err)));
                throw err;
            });
    };

export const createAiCampaign = (formData) => (dispatch) => {
    dispatch(actionRequesting());
    return createCampaign(formData)
        .then((res) => {
            dispatch(actionSuccess(res.data));
            dispatch(campaignSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(actionError(creditOrMessage(err)));
            throw err;
        });
};

export const patchAiCampaign = (id, formData) => (dispatch) => {
    dispatch(actionRequesting());
    return patchCampaign(id, formData)
        .then((res) => {
            dispatch(actionSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const deleteAiCampaign = (id) => (dispatch) => {
    dispatch(actionRequesting());
    return deleteCampaign(id)
        .then(() => {
            dispatch(removeCampaignFromList(id));
            dispatch(actionSuccess({deleted: id}));
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const cancelAiCampaign = (id) => (dispatch) => {
    dispatch(actionRequesting());
    return cancelCampaign(id)
        .then((res) => {
            const body = unwrap(res.data);
            dispatch(updateCampaignInList(body));
            dispatch(actionSuccess(body));
            return body;
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const enhanceAiPrompt = (id, payload = {}) => (dispatch) => {
    dispatch(actionRequesting());
    return enhancePrompt(id, payload)
        .then((res) => {
            const body = unwrap(res.data);
            if (body?.campaign) {
                dispatch(campaignSuccess(body.campaign));
            }
            dispatch(actionSuccess(body));
            return body;
        })
        .catch((err) => {
            dispatch(actionError(creditOrMessage(err)));
            throw err;
        });
};

export const generateAiCampaign = (id, asset = "both", payload = {}) => (dispatch, getState) =>
    runAsyncGeneration(dispatch, getState, () => generateCampaign(id, asset, payload));

export const editAiCreative = (id, instruction) => (dispatch, getState) =>
    runAsyncGeneration(dispatch, getState, () => editCreative(id, instruction));

export const regenerateAiCreative = (id) => (dispatch, getState) =>
    runAsyncGeneration(dispatch, getState, () => regenerateCreative(id));

/** Load creative history for the history dropdown (newest first). */
export const fetchCreativeVersions = (id) => (dispatch) => {
    if (!id) return Promise.resolve([]);
    dispatch(creativeVersionsRequesting());
    return listCreativeVersions(id)
        .then((res) => {
            dispatch(creativeVersionsSuccess(res.data));
            return unwrap(res.data) || [];
        })
        .catch((err) => {
            dispatch(creativeVersionsError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

/** Restore a history version onto the live campaign (image + template + reference). */
export const restoreAiCreativeVersion = (campaignId, versionId) => (dispatch) => {
    dispatch(actionRequesting());
    return restoreCreativeVersion(campaignId, versionId)
        .then((res) => {
            const campaignData = unwrap(res.data);
            if (campaignData) {
                dispatch(campaignSuccess(campaignData));
            }
            dispatch(creativeVersionSelect(versionId));
            dispatch(actionSuccess(campaignData));
            // Refresh is_current flags on tiles
            dispatch(fetchCreativeVersions(campaignId));
            return campaignData;
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

/** Download current creative blob and trigger a browser save dialog. */
export const downloadAiCreative = (id) => () =>
    downloadCreative(id).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `campaign-${id}-creative.png`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
    });

export const submitAiTemplate = (id) => (dispatch) => {
    dispatch(actionRequesting());
    return submitTemplate(id)
        .then((res) => {
            dispatch(actionSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const saveAiAudience = (id, audience, audienceFile) => (dispatch) => {
    dispatch(actionRequesting());
    return setAudience(id, audience, audienceFile)
        .then((res) => {
            dispatch(actionSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const saveAiRunMode = (id, payload) => (dispatch) => {
    dispatch(actionRequesting());
    return setRunMode(id, payload)
        .then((res) => {
            dispatch(actionSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const runAiCampaign = (id) => (dispatch) => {
    dispatch(actionRequesting());
    return runCampaign(id)
        .then((res) => {
            dispatch(actionSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const testSendAiCampaign = (id, payload) => (dispatch) => {
    dispatch(actionRequesting());
    return testSend(id, payload)
        .then((res) => {
            dispatch(actionSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(actionError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export default aiCampaignSlice.reducer;
